import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, Cell,
} from "recharts";
import { Inbox, TrendingUp, Briefcase, CheckCircle2, XCircle, DollarSign, Activity, Clock, RefreshCw, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useSettings, formatMoney } from "../lib/settings";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import InsightsFilterBar, { sizeBandToRange } from "../components/InsightsFilterBar";
import { KPICard, ForecastChart, ActivityBoard, FunnelWidget, AttainmentCard } from "../components/InsightsWidgets";
import AddTargetModal from "../components/AddTargetModal";

function Metric({ label, value, sub, icon: Icon, highlight }) {
  return (
    <div className={`metric-card ${highlight ? "metric-card-highlight" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 500 }}>{label}</div>
        {Icon && (
          <div className="flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 8, background: "var(--pg-surface-2)", color: "var(--pg-text-dim)" }}>
            <Icon size={15} strokeWidth={2} />
          </div>
        )}
      </div>
      <div className="text-4xl mt-3" style={{ color: "var(--pg-text)", fontWeight: 800, letterSpacing: "-0.035em" }}>{value}</div>
      {sub && (
        <div className="text-xs mt-2"
          style={{ color: highlight ? "var(--pg-accent-deep)" : "var(--pg-text-dim)", fontWeight: 600, letterSpacing: "-0.005em" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Temporal palette — mint accent
const ACCENT = "#6EF0DC";
const PEACH = "#3FBFAE";
const SAGE = "#8C8C8C";

export default function Insights() {
  const { user } = useAuth();
  const { currency } = useSettings();
  const canFilterOwner = user?.role !== "sales_rep";
  const [activeTab, setActiveTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [pipeBreakdown, setPipeBreakdown] = useState(null);
  const [conv, setConv] = useState([]);
  const [overTime, setOverTime] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [health, setHealth] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── v2 state ───────────────────────────────
  const [filters, setFilters] = useState({
    period: "month", owner_id: "", pipeline_id: "", source: "", deal_size_band: "", deal_kind: "",
  });
  const [exec, setExec] = useState(null);
  const [forecastRich, setForecastRich] = useState(null);
  const [activity, setActivity] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [targetOpen, setTargetOpen] = useState(false);
  const [targetKind, setTargetKind] = useState("total");
  const [forecastKind, setForecastKind] = useState("total");
  const [users, setUsers] = useState([]);

  // Load v2 data on filter change
  const loadV2 = async () => {
    const params = new URLSearchParams();
    params.set("period", filters.period);
    if (filters.owner_id) params.set("owner_id", filters.owner_id);
    if (filters.pipeline_id) params.set("pipeline_id", filters.pipeline_id);
    if (filters.source) params.set("source", filters.source);
    if (filters.deal_kind) params.set("deal_kind", filters.deal_kind);
    const size = sizeBandToRange(filters.deal_size_band);
    if (size.deal_size_min !== undefined) params.set("deal_size_min", size.deal_size_min);
    if (size.deal_size_max !== undefined) params.set("deal_size_max", size.deal_size_max);
    try {
      const [e, f, a, fn, u] = await Promise.all([
        api.get(`/insights/executive?${params}`),
        api.get(`/insights/forecast?months=12&${params}`),
        api.get(`/insights/activity?${params}`),
        api.get(`/insights/funnel?${params}`),
        users.length ? Promise.resolve({ data: users }) : api.get("/users").catch(() => ({ data: [] })),
      ]);
      setExec(e.data); setForecastRich(f.data); setActivity(a.data);
      setFunnel(fn.data); setUsers(u.data);
    } catch { /* toast handled by parent */ }
  };

  useEffect(() => { loadV2(); /* eslint-disable-next-line */ }, [filters]);
  const { theme } = useTheme();
  const tipStyle = useMemo(() => ({
    background: theme === "light" ? "#FFFFFF" : "#111114",
    border: `1px solid ${theme === "light" ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.08)"}`,
    color: theme === "light" ? "#0A0A0A" : "#FFFFFF",
    fontFamily: "Lora, Georgia, serif",
    fontSize: 12,
    borderRadius: 8,
    boxShadow: theme === "light" ? "0 8px 24px rgba(0,0,0,0.10)" : "0 12px 32px rgba(0,0,0,0.5)",
    fontWeight: 500,
  }), [theme]);
  const axisColor = theme === "light" ? "rgba(0,0,0,0.50)" : "rgba(255,255,255,0.35)";
  const gridColor = theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.05)";

  const load = async () => {
    setLoading(true);
    // Legacy v1 endpoints only understand week/month/quarter/year — map 12mo → year.
    const legacyPeriod = filters.period === "12mo" ? "year" : filters.period;
    try {
      const [s, pb, c, ot, f, ov, ph, gr, ovw] = await Promise.all([
        api.get(`/insights/summary?period=${legacyPeriod}`),
        api.get("/insights/pipeline-breakdown"),
        api.get(`/insights/conversion-by-owner?period=${legacyPeriod}`),
        api.get("/insights/won-lost-over-time"),
        api.get("/insights/forecast-simple"),
        api.get("/insights/overdue-moves-by-owner"),
        api.get("/insights/pipeline-health"),
        api.get("/insights/growth"),
        api.get("/insights/overview"),
      ]);
      setSummary(s.data); setPipeBreakdown(pb.data); setConv(c.data);
      setOverTime(ot.data); setForecast(f.data); setOverdue(ov.data);
      setHealth(ph.data);
      setGrowth(gr.data); setOverview(ovw.data);
    } catch { toast.error("Failed to load insights"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.period]);

  if (loading || !summary) return <div className="px-8 py-12 text-mute text-sm">loading…</div>;



  return (
    <div data-testid="insights-page">
      <PageHeader title="Insights" subtitle="sales performance" />

      {/* Tab bar */}
      <div className="px-8 pt-4">
        <div className="flex gap-1 border-b" style={{ borderColor: "var(--pg-border)" }}>
          {[
            { k: "overview", label: "Overview" },
            { k: "acquisition", label: "Acquisition" },
            { k: "growth", label: "Growth" },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              data-testid={`insights-tab-${t.k}`}
              className="px-4 py-2 text-sm"
              style={{
                borderBottom: activeTab === t.k ? "2px solid var(--pg-accent-deep)" : "2px solid transparent",
                color: activeTab === t.k ? "var(--pg-text)" : "var(--pg-text-mute)",
                fontWeight: activeTab === t.k ? 700 : 500,
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Sticky filter bar (Overview tab only) */}
      {activeTab === "overview" && (
        <InsightsFilterBar value={filters} onChange={setFilters} canFilterOwner={canFilterOwner} />
      )}

      {activeTab === "overview" && exec && (
        <div className="p-8 space-y-6" data-testid="insights-overview">
          {/* Row 1 · Executive KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              testid="kpi-revenue"
              label="Revenue"
              value={exec.revenue.value}
              deltaPct={exec.revenue.delta_pct}
              sparkline={exec.revenue.sparkline}
              currency={currency.symbol}
              accent="#635BFF"
            />
            <KPICard
              testid="kpi-weighted-pipeline"
              label="Weighted Pipeline"
              value={exec.weighted_pipeline.value}
              sub={`${formatMoney(exec.pipeline_value.value, currency.symbol)} unweighted`}
              currency={currency.symbol}
              accent="#00A66E"
            />
            <KPICard
              testid="kpi-win-rate"
              label="Win Rate"
              value={exec.win_rate.value}
              deltaPct={exec.win_rate.delta_pct}
              format="pct"
              accent="#F5A524"
            />
            <AttainmentCard attainment={exec.attainment} currency={currency.symbol} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <KPICard
              testid="kpi-avg-deal"
              label="Avg Deal Size"
              value={exec.avg_deal_size.value}
              deltaPct={exec.avg_deal_size.delta_pct}
              currency={currency.symbol}
              accent="#7A70FF"
            />
            <KPICard
              testid="kpi-cycle"
              label="Sales Cycle"
              value={exec.sales_cycle.value}
              deltaPct={-1 * exec.sales_cycle.delta_pct /* shorter is better */}
              format="days"
              accent="#B8B0FF"
            />
            <KPICard
              testid="kpi-pipeline"
              label="Open Pipeline"
              value={exec.pipeline_value.value}
              currency={currency.symbol}
              accent="#635BFF"
            />
          </div>

          {/* Row 2 · Forecast (with kind switcher) */}
          {forecastRich && (
            <div>
              <div className="flex gap-1 mb-3" data-testid="forecast-kind-switcher">
                {["total", "acquisition", "growth"].map(k => (
                  <button
                    key={k}
                    onClick={() => setForecastKind(k)}
                    className={`pg-btn ${forecastKind === k ? "pg-btn-active" : ""}`}
                    style={{ fontSize: 12 }}
                    data-testid={`forecast-kind-${k}`}
                  >{k[0].toUpperCase() + k.slice(1)}</button>
                ))}
              </div>
              <ForecastChart
                data={forecastRich.series}
                targetLine={forecastRich.targets[forecastKind]}
                kind={forecastKind}
                currency={currency.symbol}
                onAddTarget={(k) => { setTargetKind(k); setTargetOpen(true); }}
              />
            </div>
          )}

          {/* Row 3 · Activity + Funnel side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ActivityBoard rows={activity} />
            {funnel && (
              <FunnelWidget
                pipelineName={funnel.pipeline_name}
                stages={funnel.stages}
                currency={currency.symbol}
              />
            )}
          </div>
        </div>
      )}

      {targetOpen && canFilterOwner && (
        <AddTargetModal
          users={users}
          initialKind={targetKind}
          onClose={() => setTargetOpen(false)}
          onSaved={loadV2}
        />
      )}


      {/* Legacy revenue-by-month chart moved into Growth tab as a supporting viz */}
      {activeTab === "growth" && overview?.revenue_by_month?.length > 0 && (
        <div className="px-8 pb-2" data-testid="insights-overview-legacy-chart">
          <div className="pg-card p-6">
            <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Revenue by Month · Acquisition vs Growth</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={overview.revenue_by_month}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis dataKey="month" stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                <YAxis stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                <Tooltip contentStyle={tipStyle} />
                <Legend wrapperStyle={{ fontFamily: "Lora, serif", fontSize: 12, fontWeight: 500 }} />
                <Bar dataKey="acquisition" name="Acquisition" fill={ACCENT} radius={[6, 6, 0, 0]} />
                <Bar dataKey="growth" name="Growth" fill={PEACH} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === "growth" && growth && (
        <div className="p-8 space-y-6" data-testid="insights-growth">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Active Clients" value={growth.active_clients} icon={Briefcase} />
            <Metric label="Recurring ARR" value={formatMoney(growth.recurring_arr, currency.symbol)} icon={DollarSign} highlight />
            <Metric label="Renewal Rate · 90d" value={`${growth.renewal_rate}%`} sub={`${growth.renewals_won_90d}/${growth.renewals_won_90d + growth.renewals_lost_90d} won`} icon={RefreshCw} highlight />
            <Metric label="Churn" value={growth.churn_count} sub="Deliverables expired" icon={XCircle} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Upsell Pipeline" value={formatMoney(growth.upsell_pipeline_value, currency.symbol)} icon={TrendingUp} />
            <Metric label="Upsell Win Rate · 90d" value={`${growth.upsell_win_rate}%`} sub={`${growth.upsells_won_90d} won`} icon={CheckCircle2} />
            <Metric label="Expiring · Next 30d" value={growth.expiring_30d.count} sub={formatMoney(growth.expiring_30d.value, currency.symbol)} icon={Clock} />
            <Metric label="Expiring · Next 60d" value={growth.expiring_60d.count} sub={formatMoney(growth.expiring_60d.value, currency.symbol)} icon={Clock} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="pg-card p-6">
              <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Expiring · Next 90d</h3>
              <div className="text-3xl mb-1" style={{ fontWeight: 800, color: "var(--pg-text)" }}>
                {growth.expiring_90d.count} <span className="text-sm text-mute" style={{ fontWeight: 500 }}>deliverables</span>
              </div>
              <div className="text-sm text-mute">
                {formatMoney(growth.expiring_90d.value, currency.symbol)} recurring value
              </div>
            </div>
            <div className="pg-card p-6">
              <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Lost Renewal Reasons</h3>
              {growth.lost_reasons_breakdown.length === 0 ? (
                <div className="text-mute text-sm py-6 text-center">No lost renewals yet.</div>
              ) : (
                <table className="pg-table">
                  <thead><tr><th>Reason</th><th>Count</th></tr></thead>
                  <tbody>
                    {growth.lost_reasons_breakdown.map(r => (
                      <tr key={r.reason}>
                        <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{r.reason}</td>
                        <td>{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "acquisition" && (
      <div className="p-8 space-y-8" data-testid="insights-acquisition">
        {health && (
          <PipelineHealthCard health={health} currency={currency} />
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Metric label="Total Leads In" value={summary.total_leads} icon={Inbox} />
          <Metric label="Leads Converted" value={summary.converted_leads} sub={`+${summary.conversion_rate}% conversion`} icon={TrendingUp} highlight />
          <Metric label="Active Deals" value={summary.active_deals_count} sub={formatMoney(summary.active_deals_value, currency.symbol)} icon={Briefcase} />
          <Metric label="Deals Won" value={summary.won_count} sub={formatMoney(summary.won_value, currency.symbol)} icon={CheckCircle2} highlight />
          <Metric label="Deals Lost" value={summary.lost_count} icon={XCircle} />
          <Metric label="Avg Deal Value" value={formatMoney(summary.avg_deal_value, currency.symbol)} icon={DollarSign} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="pg-card p-6">
            <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>
              Pipeline Breakdown · {pipeBreakdown?.pipeline_name}
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={pipeBreakdown?.stages || []}>
                <CartesianGrid stroke={gridColor} vertical={false} />
                <XAxis dataKey="stage_name" stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                <YAxis stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                <Tooltip contentStyle={tipStyle} cursor={{ fill: "rgba(110,240,220,0.12)" }} />
                <Bar dataKey="count" name="Deals" radius={[8, 8, 0, 0]}>
                  {(pipeBreakdown?.stages || []).map((_, i) => (
                    <Cell key={i} fill={[ACCENT, PEACH, SAGE, ACCENT, PEACH][i % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Won vs Lost Over Time</h3>
            {overTime.length === 0 ? (
              <div className="text-mute text-sm py-10 text-center">No closed deals yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={overTime}>
                  <CartesianGrid stroke={gridColor} vertical={false} />
                  <XAxis dataKey="month" stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                  <YAxis stroke={axisColor} tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }} />
                  <Tooltip contentStyle={tipStyle} />
                  <Legend wrapperStyle={{ fontFamily: "Lora, serif", fontSize: 12, fontWeight: 500 }} />
                  <Line type="monotone" dataKey="won" stroke={ACCENT} strokeWidth={3} dot={{ r: 4, fill: ACCENT }} />
                  <Line type="monotone" dataKey="lost" stroke="#EF4444" strokeWidth={3} dot={{ r: 4, fill: "#EF4444" }} strokeDasharray="6 6" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Leads Converted by Owner</h3>
            {conv.length === 0 ? (
              <div className="text-mute text-sm py-10 text-center">No data</div>
            ) : (
              <table className="pg-table">
                <thead><tr><th>Owner</th><th>Leads</th><th>Converted</th><th>Rate</th></tr></thead>
                <tbody>
                  {conv.map(c => (
                    <tr key={c.user_id}>
                      <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{c.name}</td>
                      <td>{c.leads}</td>
                      <td style={{ color: "var(--pg-accent-deep)", fontWeight: 700 }}>{c.converted}</td>
                      <td className="text-dim text-sm">{c.leads ? Math.round(c.converted / c.leads * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="pg-card p-6">
            <h3 className="text-sm uppercase mb-4" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Overdue Moves by Owner</h3>
            {overdue.length === 0 ? (
              <div className="text-mute text-sm py-10 text-center">Nothing overdue. Nice.</div>
            ) : (
              <table className="pg-table">
                <thead><tr><th>Owner</th><th>Overdue Count</th></tr></thead>
                <tbody>
                  {overdue.map(o => (
                    <tr key={o.user_id}>
                      <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{o.name}</td>
                      <td style={{ color: "var(--pg-red)", fontWeight: 700 }}>{o.overdue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="pg-card p-6" data-testid="forecast-card">
          <h3 className="text-sm uppercase mb-3" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>Forecast</h3>
          <div className="text-xs text-mute mb-4" style={{ fontWeight: 400 }}>Open deals × probability — grouped by expected close month</div>
          {forecast.length === 0 ? (
            <div className="text-mute text-sm py-10 text-center">No open deals to forecast</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={forecast} margin={{ top: 10, right: 16, left: 0, bottom: 10 }}>
                  <CartesianGrid stroke={gridColor} vertical={false} />
                  <XAxis
                    dataKey="month"
                    stroke={axisColor}
                    tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }}
                  />
                  <YAxis
                    stroke={axisColor}
                    tick={{ fontSize: 11, fontFamily: "Lora, serif", fontWeight: 500 }}
                    tickFormatter={(v) => `${currency.symbol}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                  />
                  <Tooltip
                    contentStyle={tipStyle}
                    cursor={{ fill: "rgba(110,240,220,0.12)" }}
                    formatter={(value) => formatMoney(value, currency.symbol)}
                  />
                  <Legend wrapperStyle={{ fontFamily: "Lora, serif", fontSize: 12, fontWeight: 500 }} />
                  <Bar dataKey="open_value" name="Open Value" fill={SAGE} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="weighted_value" name="Weighted (× probability)" fill={ACCENT} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <table className="pg-table mt-6">
                <thead>
                  <tr><th>Close Month</th><th>Open Deals</th><th>Total Value</th><th>Weighted Value</th></tr>
                </thead>
                <tbody>
                  {forecast.map(f => (
                    <tr key={f.month}>
                      <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{f.month}</td>
                      <td>{f.count}</td>
                      <td>{formatMoney(f.open_value, currency.symbol)}</td>
                      <td style={{ color: "var(--pg-accent-deep)", fontWeight: 700 }}>{formatMoney(Math.round(f.weighted_value), currency.symbol)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function PipelineHealthCard({ health, currency }) {
  const { stale_deals, upcoming_renewals, lost_reasons_wow, params } = health;
  const empty = stale_deals.length === 0 && upcoming_renewals.length === 0 && lost_reasons_wow.length === 0;

  return (
    <div className="pg-card p-6" data-testid="pipeline-health-card" style={{ borderLeft: "3px solid var(--pg-accent)" }}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm uppercase" style={{ color: "var(--pg-text-mute)", letterSpacing: "0.08em", fontWeight: 600 }}>
          <Activity size={14} className="inline mr-2 -mt-0.5" />
          Pipeline Health
        </h3>
        <span className="mono text-[10px] text-mute">here&apos;s what to fix this monday</span>
      </div>
      <div className="text-xs text-mute mb-5" style={{ fontWeight: 400 }}>
        Stale deals, upcoming renewals, and lost-reason deltas — the three signals that predict next quarter&apos;s revenue.
      </div>

      {empty ? (
        <div className="text-mute text-sm py-6 text-center mono text-xs">
          All clear. Nothing stale, no renewals coming up in the next {params.renewal_window_days} days, and no loss patterns to flag.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-5">
          {/* Stale Deals */}
          <div className="space-y-3" data-testid="health-stale">
            <div className="flex items-center gap-2">
              <Clock size={14} style={{ color: "var(--pg-yellow)" }} />
              <span className="mono text-[11px] uppercase tracking-wider" style={{ color: "var(--pg-text)", fontWeight: 600 }}>
                Stale Deals
              </span>
              <span className="mono text-[10px] text-mute">{">"}{params.stale_days}d</span>
            </div>
            {stale_deals.length === 0 ? (
              <div className="text-mute mono text-[11px] py-3">Nothing stale — keep it moving.</div>
            ) : (
              <div className="space-y-2">
                {stale_deals.slice(0, 5).map((d) => (
                  <Link
                    key={d.id}
                    to={`/deals/${d.id}`}
                    className="block pg-card p-3 hover:border-purple-500"
                    style={{ background: "var(--pg-surface-2)" }}
                    data-testid={`health-stale-${d.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 pr-2">
                        <div className="text-sm truncate" style={{ color: "var(--pg-text)" }}>{d.name}</div>
                        <div className="mono text-[10px] text-mute mt-0.5 truncate">
                          {d.stage} · {formatMoney(d.value, currency.symbol)}
                        </div>
                      </div>
                      <span className="mono text-[10px]" style={{ color: "var(--pg-yellow)", whiteSpace: "nowrap" }}>
                        {d.days_in_stage}d
                      </span>
                    </div>
                  </Link>
                ))}
                {stale_deals.length > 5 && (
                  <div className="mono text-[10px] text-mute text-right">+{stale_deals.length - 5} more</div>
                )}
              </div>
            )}
          </div>

          {/* Upcoming Renewals */}
          <div className="space-y-3" data-testid="health-renewals">
            <div className="flex items-center gap-2">
              <RefreshCw size={14} style={{ color: "var(--pg-accent)" }} />
              <span className="mono text-[11px] uppercase tracking-wider" style={{ color: "var(--pg-text)", fontWeight: 600 }}>
                Upcoming Renewals
              </span>
              <span className="mono text-[10px] text-mute">{params.renewal_window_days}d</span>
            </div>
            {upcoming_renewals.length === 0 ? (
              <div className="text-mute mono text-[11px] py-3">No engagements ending soon.</div>
            ) : (
              <div className="space-y-2">
                {upcoming_renewals.slice(0, 5).map((r) => (
                  <Link
                    key={`${r.client_id}-${r.deal_id}`}
                    to={`/clients/${r.client_id}`}
                    className="block pg-card p-3 hover:border-purple-500"
                    style={{ background: "var(--pg-surface-2)" }}
                    data-testid={`health-renewal-${r.deal_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 pr-2">
                        <div className="text-sm truncate" style={{ color: "var(--pg-text)" }}>{r.company_name}</div>
                        <div className="mono text-[10px] text-mute mt-0.5 truncate">
                          {(r.engagement_type === "recurring" ? "Recurring" : "One-Off")} · {formatMoney(r.value, currency.symbol)}
                        </div>
                      </div>
                      <span className="mono text-[10px]"
                        style={{
                          color: r.days_until_end <= 14 ? "var(--pg-red)" : "var(--pg-accent-deep)",
                          whiteSpace: "nowrap",
                        }}>
                        {r.days_until_end}d
                      </span>
                    </div>
                  </Link>
                ))}
                {upcoming_renewals.length > 5 && (
                  <div className="mono text-[10px] text-mute text-right">+{upcoming_renewals.length - 5} more</div>
                )}
              </div>
            )}
          </div>

          {/* Lost Reasons WoW */}
          <div className="space-y-3" data-testid="health-lost-wow">
            <div className="flex items-center gap-2">
              <ArrowUp size={14} style={{ color: "var(--pg-red)" }} />
              <span className="mono text-[11px] uppercase tracking-wider" style={{ color: "var(--pg-text)", fontWeight: 600 }}>
                Lost Reasons WoW
              </span>
              <span className="mono text-[10px] text-mute">7d vs prev 7d</span>
            </div>
            {lost_reasons_wow.length === 0 ? (
              <div className="text-mute mono text-[11px] py-3">No deals lost in the last 14 days.</div>
            ) : (
              <div className="space-y-2">
                {lost_reasons_wow.slice(0, 5).map((w) => {
                  const Icon = w.delta > 0 ? ArrowUp : w.delta < 0 ? ArrowDown : Minus;
                  const color = w.delta > 0 ? "var(--pg-red)" : w.delta < 0 ? "var(--pg-accent-deep)" : "var(--pg-text-mute)";
                  return (
                    <div
                      key={w.reason}
                      className="pg-card p-3"
                      style={{ background: "var(--pg-surface-2)" }}
                      data-testid={`health-lost-${w.reason}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1 pr-2">
                          <div className="text-sm truncate" style={{ color: "var(--pg-text)" }}>{w.reason}</div>
                          <div className="mono text-[10px] text-mute mt-0.5">
                            {w.this_week} this · {w.last_week} last
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mono text-[11px]" style={{ color, whiteSpace: "nowrap" }}>
                          <Icon size={12} strokeWidth={2.5} />
                          {w.delta > 0 ? `+${w.delta}` : w.delta}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {lost_reasons_wow.length > 5 && (
                  <div className="mono text-[10px] text-mute text-right">+{lost_reasons_wow.length - 5} more</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

